<template>
  <v-layout wrap>
    <v-flex xs12>
      <v-toolbar dense extended color="primary" dark style="padding-top:50px">
        <v-flex xs12 pa-5>
          <v-toolbar-title slot="extension" class="display-2">
            <v-icon x-large style="padding-bottom:10px">history</v-icon>
            Job Runs
          </v-toolbar-title>
          View ingestion job run history and results.
          <v-btn flat dark :to="{ name: 'ingestionDashboard' }" class="mt-2">
            <v-icon left>arrow_back</v-icon>Back to Dashboard
          </v-btn>
        </v-flex>
      </v-toolbar>
    </v-flex>
    <v-flex xs12 pa-3>
      <v-card>
        <v-card-title>
          <v-layout row wrap align-center>
            <v-flex xs12 sm3 pa-1>
              <v-select label="Status" v-model="filter.Status" :items="statusOptions" clearable @change="onFilterChange"></v-select>
            </v-flex>
            <v-flex xs12 sm3 pa-1>
              <v-menu v-model="fromMenu" :close-on-content-click="false" offset-y>
                <template v-slot:activator="{ on }">
                  <v-text-field label="From Date" v-model="filter.from" readonly v-on="on" clearable @click:clear="filter.from=null;onFilterChange()"></v-text-field>
                </template>
                <v-date-picker v-model="filter.from" @input="fromMenu=false;onFilterChange()"></v-date-picker>
              </v-menu>
            </v-flex>
            <v-flex xs12 sm3 pa-1>
              <v-menu v-model="toMenu" :close-on-content-click="false" offset-y>
                <template v-slot:activator="{ on }">
                  <v-text-field label="To Date" v-model="filter.to" readonly v-on="on" clearable @click:clear="filter.to=null;onFilterChange()"></v-text-field>
                </template>
                <v-date-picker v-model="filter.to" @input="toMenu=false;onFilterChange()"></v-date-picker>
              </v-menu>
            </v-flex>
            <v-flex xs12 sm3 pa-1>
              <v-text-field label="Config ID" v-model="filter.configId" type="number" clearable @input="debounceFilterChange" @click:clear="onFilterChange"></v-text-field>
            </v-flex>
          </v-layout>
        </v-card-title>
        <v-data-table :headers="headers" :items="jobs" :loading="loading"
          :total-items="jobsTotalCount" :pagination.sync="pagination" :rows-per-page-items="[10, 20, 50]">
          <template slot="items" slot-scope="props">
            <td style="max-width:150px;overflow:hidden;text-overflow:ellipsis" :title="props.item.Id">
              <router-link :to="`/administration/ingestion-jobs/${props.item.Id}`">
                {{ shorten(props.item.Id) }}
              </router-link>
            </td>
            <td>
              <router-link v-if="props.item.ConfigurationId"
                :to="{ name: 'ingestionConfigs' }">
                {{ configName(props.item.ConfigurationId) }}
              </router-link>
              <span v-else>-</span>
            </td>
            <td>
              <v-chip :color="statusColor(props.item.Status)" small dark>{{ props.item.Status }}</v-chip>
            </td>
            <td>{{ formatDate(props.item.StartedAt) }}</td>
            <td>{{ formatDuration(props.item.DurationSeconds) }}</td>
            <td class="text-xs-right">
              <v-btn small color="info" depressed :to="`/administration/ingestion-jobs/${props.item.Id}`">View</v-btn>
            </td>
          </template>
        </v-data-table>
      </v-card>
    </v-flex>

    <v-snackbar :color="snackColor" :timeout="6000" top v-model="snackbar">
      {{ snackMessage }}
      <v-btn dark flat @click="snackbar = false">Close</v-btn>
    </v-snackbar>
  </v-layout>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
const ingestion = createNamespacedHelpers('ingestionManagement')

export default {
  data() {
    return {
      loading: false,
      fromMenu: false, toMenu: false,
      debounceTimer: null,
      filter: { Status: null, from: null, to: null, configId: null },
      pagination: { page: 1, rowsPerPage: 20, sortBy: 'StartedAt', descending: true },
      snackbar: false, snackColor: 'error', snackMessage: '',
      headers: [
        { text: 'Job ID', value: 'Id', align: 'left' },
        { text: 'Config', value: 'ConfigurationId', align: 'left', sortable: false },
        { text: 'Status', value: 'Status', align: 'left' },
        { text: 'Started', value: 'StartedAt', align: 'left' },
        { text: 'Duration', value: 'DurationSeconds', align: 'left', sortable: false },
        { text: '', value: null, align: 'right', sortable: false },
      ],
      statusOptions: ['Queued', 'Running', 'Completed', 'Failed'],
      refreshTimer: null,
      _ready: false,
    }
  },
  computed: {
    ...ingestion.mapGetters(['jobs', 'jobsTotalCount', 'configurations']),
  },
  watch: {
    'pagination.page'() { if (this._ready) this.loadData() },
    'pagination.rowsPerPage'() { if (this._ready) this.loadData() },
    '$route.query'(query) {
      this.filter.configId = query.configId || null
      this.filter.from = query.from || null
      this.filter.to = query.to || null
      this.onFilterChange()
    },
  },
  methods: {
    ...ingestion.mapActions(['fetchJobRuns', 'fetchConfigurations']),
    configName(configId) {
      if (!configId) return '-'
      const cfg = this.configurations.find(c => c.Id === configId)
      return cfg ? cfg.Name : `#${configId}`
    },
    statusColor(s) {
      return { Queued: 'grey', Running: 'info', Completed: 'success', Failed: 'error' }[s] || 'grey'
    },
    formatDate(d) { return d ? new Date(d).toLocaleString() : '' },
    formatDuration(sec) {
      if (sec == null) return '-'
      const m = Math.floor(sec / 60); const s = Math.floor(sec % 60)
      return m > 0 ? `${m}m ${s}s` : `${s}s`
    },
    shorten(id) { return id ? id.substring(0, 8) + '...' : '' },
    toUtcFromLocal(dateStr, isEndOfDay) {
      const [y, m, d] = dateStr.split('-').map(Number)
      return new Date(y, m - 1, d, isEndOfDay ? 23 : 0, isEndOfDay ? 59 : 0, isEndOfDay ? 59 : 0, isEndOfDay ? 999 : 0).toISOString()
    },
    buildParams() {
      const p = { page: this.pagination.page, pageSize: this.pagination.rowsPerPage }
      if (this.filter.Status) p.status = this.filter.Status
      if (this.filter.from) p.from = this.toUtcFromLocal(this.filter.from, false)
      if (this.filter.to) p.to = this.toUtcFromLocal(this.filter.to, true)
      if (this.filter.configId) p.configId = this.filter.configId
      return p
    },
    loadData() {
      this.loading = true
      this.fetchJobRuns({ params: this.buildParams() }).finally(() => {
        this.loading = false
        this._ready = true
      })
      this.scheduleRefresh()
    },
    scheduleRefresh() {
      clearTimeout(this.refreshTimer)
      const hasRunning = this.jobs.some(j => j.Status === 'Running' || j.Status === 'Queued')
      if (hasRunning) this.refreshTimer = setTimeout(() => this.loadData(), 10000)
    },
    onFilterChange() {
      if (this.pagination.page !== 1) {
        this.pagination.page = 1
      } else {
        this.loadData()
      }
    },
    debounceFilterChange() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => this.onFilterChange(), 500)
    },
  },
  mounted() {
    const q = this.$route.query
    if (q.configId) this.filter.configId = q.configId
    if (q.from) this.filter.from = q.from
    if (q.to) this.filter.to = q.to
    this.loadData(); this.fetchConfigurations()
  },
  beforeDestroy() { clearTimeout(this.refreshTimer); clearTimeout(this.debounceTimer) },
}
</script>
